import React from 'react';
import { twMerge } from 'tailwind-merge';
import { useAppSelector } from 'flow/hooks/useRedux';
import { TicketKey } from 'flow/types/EntityKeys';
import { selectOriginDTOSubtype } from 'flow/selectors/TicketsSelectors';
import Skeleton from 'flow/components/ui-components/Skeleton';

interface Props {
    ticketKey?: TicketKey;
    dateLabelBgClassName?: string;
}

const EventsListSkeletonV2 = ({ ticketKey, dateLabelBgClassName }: Props) => {
    const dtoSubtype = useAppSelector((state) =>
        selectOriginDTOSubtype(state, ticketKey),
    );

    const showCallSkeletons =
        !dtoSubtype || dtoSubtype === 'queuechannel' || dtoSubtype === 'call';

    return (
        <div className="animate-fade flex w-full flex-col">
            <DateDividerMimic bgClassName={dateLabelBgClassName} />
            {showCallSkeletons ? (
                <CallEventCardMimic />
            ) : (
                <>
                    <MessageMimic textWidthClassName="w-44" />
                    <MessageMimic textWidthClassName="w-64" />
                </>
            )}
        </div>
    );
};

export default EventsListSkeletonV2;

const DateDividerMimic = ({ bgClassName }: { bgClassName?: string }) => (
    <div className="relative w-full pt-3">
        <div className="relative w-full">
            <div className="absolute start-0 top-1/2 z-0 h-px w-full bg-gray-200" />
            <div className="flex w-full justify-center">
                <div
                    className={twMerge(
                        'relative flex h-4 items-center rounded bg-white px-3',
                        bgClassName,
                    )}
                >
                    <Skeleton.Text className="h-3.5 w-16 text-gray-200" />
                </div>
            </div>
        </div>
    </div>
);

const CallEventCardMimic = () => (
    <div className="flex flex-col gap-1 py-3 text-gray-200">
        <div className="flex h-5 items-center gap-2">
            <Skeleton.Icon className="size-4" />
            <Skeleton.Text className="h-3.5 w-28" />
            <span className="flex-1" />
            <Skeleton.Text className="h-3.5 w-10" />
        </div>
        <div className="bg-gray-150 flex flex-col rounded-xl p-3">
            <div className="flex h-4 items-center">
                <Skeleton.Text className="h-3.5 w-16" />
            </div>
            <div className="flex h-6 items-center">
                <Skeleton.Text className="h-3.5 w-32" />
            </div>
            <div className="flex h-6 items-center">
                <Skeleton.Text className="h-3.5 w-28" />
            </div>
            <div className="flex h-6 items-center">
                <Skeleton.Text className="h-3.5 w-56" />
            </div>
            <div className="flex h-6 items-center">
                <Skeleton.Text className="h-3.5 w-44" />
            </div>
        </div>
    </div>
);

interface MessageMimicProps {
    textWidthClassName: string;
}

const MessageMimic = ({ textWidthClassName }: MessageMimicProps) => (
    <div className="flex flex-col gap-1 py-3 text-gray-200">
        <div className="flex h-6 items-center gap-2 px-3">
            <Skeleton.Avatar className="size-6 rounded-full" />
            <Skeleton.Text className="h-3.5 w-24" />
            <span className="flex-1" />
            <Skeleton.Text className="h-3.5 w-10" />
        </div>
        <div className="flex h-5 items-center ps-11 pe-3">
            <Skeleton.Text className={twMerge('h-3.5', textWidthClassName)} />
        </div>
    </div>
);
